<template>
    <!-- ITDEVELOP-2722: ui/Dialog replaces the scrim div + styled <dialog>.
         Position/width overrides keep the pre-migration footprint (anchored at
         10% from the top, not centered). Keeps its own ✕ header button, so the
         primitive's built-in one is hidden. -->
    <Dialog :open="true" @update:open="(open) => !open && $emit('close')">
        <DialogContent
            name="contact info"
            hide-close-button
            class="top-[10%] max-h-[80vh] max-w-140 translate-y-0"
            :aria-describedby="undefined"
        >
            <div>
                <div class="flex items-center justify-between">
                    <!-- font-normal keeps the h1 base-layer weight. -->
                    <DialogTitle as="h1" class="text-2xl font-normal">Help pages</DialogTitle>
                    <button class="btn hover:bg-primary" @click="$emit('close')">
                        <i class="i-exit block" />
                    </button>
                </div>
            </div>
            <div class="gap-sm ul-fix flex flex-col">
                <h2>Contact information</h2>
                <p>We love helping people! Our team is dedicated to ensuring you have the best experience possible.</p>
                <p>You have the following options to get help:</p>
                <p>Find the answer in one of the trainings or step-by-step guides:</p>
                <div v-for="link in filteredDocumentationLinks" :key="link.url" class="gap-xs pl-sm flex items-center">
                    <div class="p-2xs rounded-xsm flex items-center justify-center bg-soft">
                        <i class="i-book grayscale" />
                    </div>
                    <a class="text-base underline" :href="link.url" target="_blank">{{ link.name }}</a>
                </div>
                <p>
                    If none of the above answers your questions or you want to report an issue in the Service Portal
                    please feel free to raise a help request:
                </p>
            </div>
            <div class="pb-sm border-b border-(--border-strong)">
                <Button class="btn w-full" @click.stop="$emit('open-help-feedback')"> Raise a help request </Button>
            </div>
            <div class="gap-sm flex flex-col">
                <div>
                    <p>
                        For urgent on-site situations such as security incidents, physical access issues or any
                        emergency requiring immediate presence at one of our facilities, please contact
                        <span class="font-semibold">atNorth Security Operation Center (SOC):</span>
                    </p>
                </div>
                <div
                    v-for="contact in urgentSupportContacts"
                    :key="contact.country"
                    class="gap-xs flex items-center justify-between"
                >
                    <div class="gap-xs flex items-center">
                        <div
                            class="flex h-5 w-7 items-center justify-center overflow-hidden bg-soft shadow-xs ring-1 ring-(--border-strong)"
                        >
                            <img
                                :src="contact.flagSrc"
                                :alt="`${contact.country} flag`"
                                class="h-full w-full object-cover"
                            />
                        </div>
                        <h3>{{ contact.country }}</h3>
                    </div>
                    <a class="text-base underline" :href="contact.phoneHref">{{ contact.phoneDisplay }}</a>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { computed, onMounted, onUnmounted } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import { useAdminStore } from '@/stores/admin';
import icelandFlag from '@/assets/icons/flags/iceland.svg';
import swedenFlag from '@/assets/icons/flags/sweden.svg';
import finlandFlag from '@/assets/icons/flags/finland.svg';
import denmarkFlag from '@/assets/icons/flags/denmark.svg';

const emit = defineEmits(['close', 'open-help-feedback']);

const organizationStore = useOrganizationStore();
const adminStore = useAdminStore();

const urgentSupportContacts = [
    {
        country: 'Iceland',
        phoneDisplay: '+354 5393999',
        phoneHref: 'tel:+3545393999',
        flagSrc: icelandFlag,
    },
    {
        country: 'Sweden',
        phoneDisplay: '+46 8 910019',
        phoneHref: 'tel:+468910019',
        flagSrc: swedenFlag,
    },
    {
        country: 'Finland',
        phoneDisplay: '+358 401834802',
        phoneHref: 'tel:+358401834802',
        flagSrc: finlandFlag,
    },
    {
        country: 'Denmark',
        phoneDisplay: '+45 40407777',
        phoneHref: 'tel:+4540407777',
        flagSrc: denmarkFlag,
    },
] as const;

const documentationLinks = [
    {
        name: 'What is atNorth Service Portal',
        url: 'https://elearning.easygenerator.com/77391438-ddcd-4bbd-8a6b-d133efa72e6a/',
        orgRoles: [],
        systemRoles: ['customer', 'contractor'],
    },
    {
        name: 'Welcome to Service Portal',
        url: 'https://atnorth.learnupon.com/content-details/4981346/0',
        orgRoles: [],
        systemRoles: ['employee', 'admin', 'cdim-admin', 'noc-admin'],
    },
    {
        name: 'How to manage role, group and user access',
        url: 'https://elearning.easygenerator.com/9cd9a8f1-3de2-4683-b331-20bf4750173c/',
        orgRoles: ['org-admin'],
        systemRoles: ['customer', 'contractor'],
    },
    {
        name: 'How to request permanent access card',
        url: 'https://app.tango.us/app/workflow/How-to-request-permanent-access-card-1530b3bf2f8e437a8215a1bd89130fd0',
        orgRoles: ['org-admin'],
        systemRoles: ['customer'],
    },
    {
        name: 'How to submit a visit request',
        url: 'https://app.tango.us/app/workflow/How-to-submit-a-visit-request-138ce07917584d889837114b200972e1',
        orgRoles: ['user-ticket-creator'],
        systemRoles: ['customer', 'contractor', 'employee', 'admin', 'cdim-admin', 'noc-admin'],
    },
    {
        name: 'How to submit a support request',
        url: 'https://app.tango.us/app/workflow/How-to-submit-a-support-request-8ad8b695260a4ac5ab9ba9b1333b114b',
        orgRoles: ['user-ticket-creator'],
        systemRoles: ['customer', 'employee', 'admin', 'cdim-admin', 'noc-admin'],
    },
    {
        name: 'Introduction to incident management in atNorth',
        url: 'https://atnorth.learnupon.com/content-details/4981348/0',
        orgRoles: ['user-incident-creator', 'user-incident-viewer'],
        systemRoles: ['employee', 'admin', 'cdim-admin', 'noc-admin'],
    },
    {
        name: 'How to set up notifications',
        url: 'https://app.tango.us/app/workflow/How-to-set-up-notifications-51ad47f1bd8c44c59ab0e2a3c7407617',
        orgRoles: ['notification-portal'],
        systemRoles: ['customer'],
    },
    {
        name: 'How to create a cross connection request',
        url: 'https://app.tango.us/app/workflow/How-to-create-a-cross-connection-request-5dd66e93e1474de8a2ddb7e6430164ed',
        orgRoles: ['user-ticket-creator', 'cross-connection-viewer'],
        systemRoles: ['customer'],
    },
];

const filteredDocumentationLinks = computed(() => {
    const userSystemRoles = adminStore.userCPRoles;
    const userOrgRoles = organizationStore.currentUserRoles.map((r) => r.name);
    const organizationRoles = organizationStore.currentOrgRoles.map((r) => r.name);
    const isOrgAdmin = organizationStore.isOrgAdmin;
    const availableOrgRoles = isOrgAdmin ? [...new Set([...organizationRoles, ...userOrgRoles])] : userOrgRoles;

    return documentationLinks.filter((link) => {
        const hasSystemRole = link.systemRoles.some((role) => userSystemRoles.includes(role));
        if (!hasSystemRole) return false;

        if (link.orgRoles.length === 0) return true;

        return link.orgRoles.some((role) => availableOrgRoles.includes(role));
    });
});

// Escape and outside-click are owned by ui/Dialog now. Enter-to-close is a
// pre-existing quirk of this popup, kept as-is.
function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
        emit('close');
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeyPress);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyPress);
});
</script>

<style scoped>
.ul-fix {
    ul {
        list-style-type: disc;
        margin-left: 1rem;
    }
}
</style>
